/**
 * Email Connector configuration component.
 * Specialized configuration for Email connector (Microsoft Graph / Gmail).
 */

// React Imports
import { useState } from 'react';
import { CoordioDropdown, Input } from '@coordio/ui';
import { OAuthConnectButton } from './OAuthConnectButton';

export interface EmailConnectorConfigProps {
  instanceId?: string;
  settingsJson?: string;
  isConnected?: boolean;
  onChange?: (settingsJson: string) => void;
  onConnected?: () => void;
}

/**
 * Email Connector configuration component.
 */
export const EmailConnectorConfig = ({
  instanceId,
  settingsJson = '{}',
  isConnected = false,
  onChange,
  onConnected,
}: EmailConnectorConfigProps) => {
  const [provider, setProvider] = useState<'microsoft' | 'gmail'>('microsoft');
  const [defaultFolder, setDefaultFolder] = useState('inbox');
  const [defaultFromAddress, setDefaultFromAddress] = useState('');

  const handleProviderChange = (newProvider: 'microsoft' | 'gmail') => {
    setProvider(newProvider);
    const settings = JSON.parse(settingsJson || '{}');
    settings.provider = newProvider;
    onChange?.(JSON.stringify(settings));
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="p-6">
        <h6 className="text-lg font-semibold tracking-tight mb-6">
          Email Provider Configuration
        </h6>

        <div className="mb-6">
          <p className="text-sm text-muted-foreground mb-2">
            Select Provider
          </p>
          <div className="inline-flex w-full rounded-md border" role="group">
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium border-r transition-colors rounded-l-md ${
                provider === 'microsoft' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
              }`}
              onClick={() => handleProviderChange('microsoft')}
            >
              Microsoft Graph (Office 365)
            </button>
            <button
              type="button"
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors rounded-r-md ${
                provider === 'gmail' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'
              }`}
              onClick={() => handleProviderChange('gmail')}
            >
              Gmail API
            </button>
          </div>
        </div>

        {provider === 'microsoft' && (
          <div className="flex flex-col gap-6">
            {instanceId && (
              <OAuthConnectButton
                instanceId={instanceId}
                connectorName="Microsoft Graph"
                isConnected={isConnected}
                onConnected={onConnected}
              />
            )}
            <div className="space-y-1">
              <label className="text-sm font-medium">Default Folder</label>
              <CoordioDropdown
                options={[
                  { value: 'inbox', label: 'Inbox' },
                  { value: 'sent', label: 'Sent Items' },
                  { value: 'drafts', label: 'Drafts' },
                  { value: 'archive', label: 'Archive' },
                ]}
                value={defaultFolder}
                onChange={(val) => {
                  const folder = val ?? 'inbox';
                  setDefaultFolder(folder);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultFolder = folder;
                  onChange?.(JSON.stringify(settings));
                }}
                size="sm"
                clearable={false}
              />
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800/40 dark:bg-blue-950/50 dark:text-blue-300">
              Microsoft Graph requires OAuth 2.0 authentication with Mail.Read and Mail.ReadWrite permissions.
            </div>
          </div>
        )}

        {provider === 'gmail' && (
          <div className="flex flex-col gap-6">
            {instanceId && (
              <OAuthConnectButton
                instanceId={instanceId}
                connectorName="Gmail"
                isConnected={isConnected}
                onConnected={onConnected}
              />
            )}
            <div className="space-y-1">
              <label className="text-sm font-medium">Default Label</label>
              <Input
                type="text"
                className="w-full"
                value={defaultFolder}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setDefaultFolder(e.target.value);
                  const settings = JSON.parse(settingsJson || '{}');
                  settings.defaultLabel = e.target.value;
                  onChange?.(JSON.stringify(settings));
                }}
              />
              <p className="text-xs text-muted-foreground">Default Gmail label/folder (e.g., INBOX, SENT)</p>
            </div>
            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-800/40 dark:bg-blue-950/50 dark:text-blue-300">
              Gmail API requires OAuth 2.0 authentication with gmail.readonly and gmail.compose scopes.
            </div>
          </div>
        )}

        <div className="mt-6 pt-6 border-t border-border">
          <h6 className="text-lg font-semibold tracking-tight mb-4">
            Draft Settings
          </h6>
          <div className="space-y-1">
            <label className="text-sm font-medium">Default From Address</label>
            <Input
              type="text"
              className="w-full"
              value={defaultFromAddress}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setDefaultFromAddress(e.target.value);
                const settings = JSON.parse(settingsJson || '{}');
                settings.defaultFromAddress = e.target.value;
                onChange?.(JSON.stringify(settings));
              }}
            />
            <p className="text-xs text-muted-foreground">Default email address for sending drafts (optional)</p>
          </div>
        </div>
      </div>
    </div>
  );
};
